import * as z from 'zod'

import { AgeType } from '#domain/types'

import {
  ExperienceKey,
  FIN_EXPERIENCE_BY_KEY,
  type FinExperience,
  finExperienceKeySchema,
  FinGroups,
} from '../finExperiences'
import raw from './finCategories.json'

// How the calculator taxes an income record. Each FinCategory that
// represents an income stream tags itself with the taxation type; the
// Taxer buckets records by this to compute federal, state, SE, and
// surtax lines. Values match the labels used by the tax code (W-2
// wages, LTCG, qualified dividends, etc.).
export enum IncomeTaxationType {
  AMT_ADD_BACKS = 'amtAddBacks',
  INTEREST_ORDINARY_DIVIDENDS = 'interestOrdinaryDividends',
  K1_INCOME = 'k1Income',
  LONG_TERM_CAPITAL_GAINS = 'longTermCapitalGains',
  OTHER_INCOME = 'otherIncome',
  PENSION_IRA_DISTRIBUTIONS = 'pensionIraDistributions',
  QUALIFIED_DIVIDENDS = 'qualifiedDividends',
  RENTAL_INCOME = 'rentalIncome',
  SELF_EMPLOYMENT_GROSS_REVENUE = 'selfEmploymentGrossRevenue',
  SHORT_TERM_CAPITAL_GAINS = 'shortTermCapitalGains',
  SOCIAL_SECURITY_BENEFITS = 'socialSecurityBenefits',
  TAX_EXEMPT_INTEREST = 'taxExemptInterest',
  W2_WAGES = 'w2Wages',
}

// The most granular level of the classification tree: every
// FinCategory belongs to one FinExperience via `experienceKey`,
// and every FinancialRecord belongs to one FinCategory. Engine code
// queries this layer most heavily — tax lines, contribution buckets,
// double-entry offsets, RMD cascades, draw-down orderings all resolve
// through it.

export const POSSIBLE_FIN_CATEGORY_KEYS = [
  'catAssetsBonds',
  'catAssetsBrokerage', // engine
  'catAssetsCash', // engine — double-entry anchor for Free Cash virtual account
  'catAssetsCCorp', // yml
  'catAssetsChecking', // yml
  'catAssetsCollectables', // yml
  'catAssetsCommercialBuilding', // yml
  'catAssetsCrypto', // yml
  'catAssetsDeathBenefit',
  'catAssetsDeposit', // yml
  'catAssetsESOP', // yml
  'catAssetsESPP', // yml
  'catAssetsGroupLife',
  'catAssetsIndexedUniversalLife',
  'catAssetsIra401k403b', // yml = former part of catAssetsIra401k403b457b
  'catAssetsIra401k403bRoth', // yml = former part of catAssetsIra401k403b457bRoth
  'catAssetsIra401kSolo', // yml
  'catAssetsIra401kSoloRoth',
  'catAssetsIra457b', // yml = former part of catAssetsIra401k403b457b
  'catAssetsIra457bRoth', // yml = former part of catAssetsIra401k403b457bRoth
  'catAssetsIraInherited', // yml
  'catAssetsIraRollover', // yml
  'catAssetsIraRoth', // yml
  'catAssetsIraSep', // yml
  'catAssetsIraSimple', // yml
  'catAssetsIraTraditional', // yml
  'catAssetsIraTsp', // yml
  'catAssetsIraTspRoth', // yml
  'catAssetsLand', // yml
  'catAssetsLLC', // yml
  'catAssetsMoneyMarket', // yml
  'catAssetsOther', // yml
  'catAssetsPartnership', // yml
  'catAssetsPrimaryResidence', // yml
  'catAssetsRentalRealEstate', // yml
  'catAssetsRSU', // yml
  'catAssetsSavings529', // yml
  'catAssetsSavingsFsa', // yml
  'catAssetsSavingsHealth',
  'catAssetsSavingsRegular', // yml
  'catAssetsSCorp', // yml
  'catAssetsSecondaryResidence', // ZL-1176 — secondary home, clone of catAssetsPrimaryResidence
  'catAssetsSoleProprietorship', // yml
  'catAssetsStocks',
  'catAssetsTermLife',
  'catAssetsVehicle', // yml
  'catAssetsWholeLife', // yml
  'catContributionsAuto', // engine — reinvestment double-entry offset, net cash $0
  'catContributionsEmployer', // yml, engine — routes to companyContributionValue, suppresses TimelineRecord
  'catContributionsOwner', // yml
  'catCoverageDeathBenefit', // yml
  'catCoverageMedicareBenefit', // yml
  'catCoveragePetLimit', // yml
  'catCoveragePropertyLimit', // yml
  'catCoverageUmbrellaLimit', // yml
  'catCoverageVehicleLimit', // yml
  'catExpensesAutoInsurance', // yml
  'catExpensesBusinessCosts',
  'catExpensesCar', // yml
  'catExpensesCharity', // yml
  'catExpensesChildcare', // yml
  'catExpensesCreditInterest', // engine — LiabilityGenerator interest portion of each payment
  'catExpensesCreditPrincipal', // engine — LiabilityGenerator principal portion of each payment
  'catExpensesDailyLiving', // yml
  'catExpensesDisabilityInsurance', // yml
  'catExpensesEducation',
  'catExpensesElectricity', // yml
  'catExpensesEntertainment', // yml
  'catExpensesGas', // yml
  'catExpensesGifts', // yml
  'catExpensesGrocery', // yml
  'catExpensesHealthInsurance', // yml
  'catExpensesHoa', // yml
  'catExpensesHomeInsurance', // yml
  'catExpensesInsurance', // yml
  'catExpensesInternet', // yml
  'catExpensesLandscape', // yml
  'catExpensesLTCInsurance', // yml
  'catExpensesMedical', // yml
  'catExpensesMedicare',
  'catExpensesMobile', // yml
  'catExpensesOther', // yml
  'catExpensesPetInsurance', // yml
  'catExpensesPets', // yml
  'catExpensesPurchase', // yml
  'catExpensesRenovation', // yml
  'catExpensesRent', // yml
  'catExpensesRentersInsurance', // yml
  'catExpensesShopping', // yml
  'catExpensesShortTermDisability', // yml
  'catExpensesSubscription', // yml
  'catExpensesTransportation', // yml
  'catExpensesTrash', // yml
  'catExpensesTraveling', // yml
  'catExpensesTV',
  'catExpensesUmbrellaInsurance', // yml
  'catExpensesVacation',
  'catExpensesWater', // yml
  'catIncomeAnnuity', // yml
  'catIncomeBonus', // yml
  'catIncomeCapitalGains',
  'catIncomeDividendsOrdinary',
  'catIncomeDividendsQualified',
  'catIncomeEntrepreneurshipActive', // yml = former catIncomeEntrepreneurshipNet
  'catIncomeEntrepreneurshipGuaranteedPayments', // yml
  'catIncomeEntrepreneurshipPassive', // yml
  'catIncomeEntrepreneurshipSCorpK1', // yml
  'catIncomeGifts', // yml
  'catIncomeInheritance', // yml
  'catIncomeISOBargainElement', // AMT preference item — ISO exercise spread; reference-only until engine wires AMT
  'catIncomeOther', // yml
  'catIncomePension', // yml
  'catIncomeRentalNet', // yml
  'catIncomeRequiredDistributionsTaxable', // engine — triggers RMD cascade from a_isRequiredDistributionsBase assets
  'catIncomeRequiredDistributionsTaxExempt',
  'catIncomeRoyaltyNet',
  'catIncomeRSU', // yml
  'catIncomeSalary', // yml
  'catIncomeSocialSecurity', // yml
  'catIncomeTaxExempt',
  'catIncomeTips', // yml
  'catIncomeWithdrawalTaxable',
  'catIncomeWithdrawalTaxExempt', // yml
  'catIncomeYieldsTaxable',
  'catIncomeYieldsTaxExempt',
  'catLiabilitiesCar', // yml
  'catLiabilitiesCard', // yml
  'catLiabilitiesHeloc', // yml
  'catLiabilitiesLease', // yml
  'catLiabilitiesLocBusiness', // yml
  'catLiabilitiesLocPersonal', // yml
  'catLiabilitiesMortgage', // yml
  'catLiabilitiesOther', // yml
  'catLiabilitiesPledgedAssetLine', // yml
  'catLiabilitiesStudent', // yml
  'catTaxesAlternativeMinimumFederal',
  'catTaxesCapitalGains',
  'catTaxesCreditFederal', // engine — AnnualTaxes credit offset step
  'catTaxesCreditState',
  'catTaxesDeductionsItemized',
  'catTaxesDeductionsStandard',
  'catTaxesIncomeFederal',
  'catTaxesIncomeState',
  'catTaxesMedicareAdditionalFederal',
  'catTaxesMedicareEntrepreneurshipFederal',
  'catTaxesMedicareW2Federal',
  'catTaxesNetInvestmentIncomeFederal',
  'catTaxesProperty',
  'catTaxesSocialSecurityEntrepreneurshipFederal',
  'catTaxesSocialSecurityW2Federal',
  'catTaxesTaxExempt',
  'catTechnicalQuestScenario', // yml — isTechnical: true, no timeline records generated
  'catTechnicalRothConversion',
  'catTechnicalVirtualBalance', // engine — isTechnical Free Cash account, double-entry target for catAssetsCash
] as const

export type FinCategoryKey = (typeof POSSIBLE_FIN_CATEGORY_KEYS)[number]

export const finCategoryKeySchema = z.enum([...POSSIBLE_FIN_CATEGORY_KEYS])

// Named constants for record category keys — call sites read
// `CategoryKey.catAssetsCash` instead of the raw string literal.
export const CategoryKey = finCategoryKeySchema.enum

const nullableNumberSchema = z.number().nullable()

const ageTypeSchema = z.enum([
  AgeType.AGE_NOW,
  AgeType.AGE_DEATH,
  AgeType.AGE_RETIREMENT,
  AgeType.AGE_SOCIAL_SECURITY,
  AgeType.AGE_REQUIRED_DISTRIBUTIONS,
  AgeType.AGE_IN_A_YEAR,
])

const incomeTaxationTypeSchema = z
  .enum([
    IncomeTaxationType.AMT_ADD_BACKS,
    IncomeTaxationType.INTEREST_ORDINARY_DIVIDENDS,
    IncomeTaxationType.K1_INCOME,
    IncomeTaxationType.LONG_TERM_CAPITAL_GAINS,
    IncomeTaxationType.OTHER_INCOME,
    IncomeTaxationType.PENSION_IRA_DISTRIBUTIONS,
    IncomeTaxationType.QUALIFIED_DIVIDENDS,
    IncomeTaxationType.RENTAL_INCOME,
    IncomeTaxationType.SELF_EMPLOYMENT_GROSS_REVENUE,
    IncomeTaxationType.SHORT_TERM_CAPITAL_GAINS,
    IncomeTaxationType.SOCIAL_SECURITY_BENEFITS,
    IncomeTaxationType.TAX_EXEMPT_INTEREST,
    IncomeTaxationType.W2_WAGES,
  ])
  .nullable()

// TODO tightening candidates:
//   - `a_drawDownOrder`: `z.number().int().positive().nullable()`
//   - Cross-category FK validity as Zod refinements (currently
//     enforced by tests at build time).
export const finCategorySchema = z.object({
  name: z.string(),
  experienceKey: finExperienceKeySchema,
  rate: z.number(),
  startAtAge: ageTypeSchema,
  endAtAge: ageTypeSchema,
  isTechnical: z.boolean(),
  isActiveIncome: z.boolean(),
  doubleEntryCategoryKey: finCategoryKeySchema.nullable(),
  key: finCategoryKeySchema,
  i_taxationType: incomeTaxationTypeSchema,
  // Declared, shown in the playground, read by nothing. How *discretionary* a
  // cost is — 1 is a vacation, 0 is car insurance — which is a different
  // question from whether the What-If lever can scale it: car insurance scores
  // 0 here and is fully adjustable. Kept for the smarter lever it was meant for.
  adjustmentWeight: nullableNumberSchema,
  /**
   * Whether the What-If spend-less lever may scale records in this category.
   *
   * False for the three the engine charges on the user's behalf — a loan's
   * interest and principal, and property tax — which reach the Expenses group
   * without any record of the user's own behind them. Absent means false, so a
   * new engine-generated category cannot default its way into the lever's reach.
   */
  e_isAdjustable: z.boolean().nullish(),
  a_isRequiredDistributionsBase: z.boolean(),
  a_isLiquid: z.boolean(),
  a_topUpCategoryKey: finCategoryKeySchema.nullable(),
  a_closeCategoryKey: finCategoryKeySchema.nullable(),
  a_recurrentInflowCategoryKey: finCategoryKeySchema.nullable(),
  a_drawDownOrder: nullableNumberSchema,
  a_withdrawalCategoryKey: finCategoryKeySchema.nullable(),
})

export type FinCategory = z.infer<typeof finCategorySchema>

export const FIN_CATEGORIES: readonly FinCategory[] = z
  .array(finCategorySchema)
  .parse(raw)

// O(1) lookup by key. Exhaustive over `FinCategoryKey` — every
// category in source carries a unique key.
export const FIN_CATEGORY_BY_KEY: ReadonlyMap<FinCategoryKey, FinCategory> =
  new Map(FIN_CATEGORIES.map(c => [c.key, c]))

// Cross-table joins. Kept small: only the experience lookup + a
// handful of group/experience predicates the engine branches on.
// Straight property reads (`category.rate`) stay at the call site.

export const getCategoryExperience = (category: FinCategory): FinExperience =>
  FIN_EXPERIENCE_BY_KEY[category.experienceKey]

export const isAsset = (category: FinCategory): boolean =>
  getCategoryExperience(category).finGroup === FinGroups.Assets

/**
 * What the spend-less lever may move, and what `adjustRecords` scales — one
 * declaration, so the two cannot drift into disagreeing.
 */
export const isAdjustableExpense = (category: FinCategory): boolean =>
  category.e_isAdjustable === true

export const isExpense = (category: FinCategory): boolean =>
  getCategoryExperience(category).finGroup === FinGroups.Expenses

export const isContribution = (category: FinCategory): boolean =>
  getCategoryExperience(category).finGroup === FinGroups.Contributions

export const isTechnical = (category: FinCategory): boolean =>
  getCategoryExperience(category).finGroup === FinGroups.Technical

export const isRealEstate = (category: FinCategory): boolean =>
  getCategoryExperience(category).key === ExperienceKey.RealEstate
